import React from 'react'
import { useTranslation } from 'react-i18next'
import { MoreVertical, Paperclip, Pencil, Plus, Trash2, Trophy } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatIsoDate } from '../../schedule/lib/format'
import { formatMoney } from '../lib/money'
import { canAwardQuote, type RfqAffordances } from '../lib/rfq'
import type { CostRfqQuote } from '../types'

interface Props {
  quotes: CostRfqQuote[]
  af: RfqAffordances
  currency: string
  locale: string
  onAdd: () => void
  onEdit: (quote: CostRfqQuote) => void
  onDelete: (quote: CostRfqQuote) => void
  onAward: (quote: CostRfqQuote) => void
  onFiles: (quote: CostRfqQuote) => void
}

/** Part D spec §8: one row per asked supplier — amount, date, note, attachments — with "Vybrat vítěze" and the winner. */
const RfqQuotesCard: React.FC<Props> = ({ quotes, af, currency, locale, onAdd, onEdit, onDelete, onAward, onFiles }) => {
  const { t } = useTranslation()
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base">{t('CostControl_rfq_quotes_title')}</CardTitle>
        {af.editQuotes && (
          <Button variant="outline" size="sm" onClick={onAdd}>
            <Plus className="h-4 w-4" />
            {t('CostControl_rfq_quote_add')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {quotes.length === 0 ? <p className="text-sm text-muted-foreground">{t('CostControl_rfq_quotes_empty')}</p> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('CostControl_rfq_quote_supplier')}</TableHead>
                <TableHead className="w-40 text-right">{t('CostControl_rfq_quote_amount')}</TableHead>
                <TableHead className="w-28">{t('CostControl_rfq_quote_receivedAt')}</TableHead>
                <TableHead>{t('CostControl_rfq_quote_note')}</TableHead>
                <TableHead className="w-32" />
                <TableHead className="w-52 text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.map((q) => (
                <TableRow key={q.id}>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{q.supplierName}</span>
                      {q.isWinner && <Badge variant="success">{t('CostControl_rfq_quote_winner')}</Badge>}
                    </div>
                    {q.supplierIco && <div className="text-xs text-muted-foreground">{t('CostControl_contract_field_ico')} {q.supplierIco}</div>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {q.amount !== null ? formatMoney(q.amount, { locale, currency }) : <span className="text-muted-foreground">{t('CostControl_rfq_quote_no_amount')}</span>}
                  </TableCell>
                  <TableCell className="text-sm">{q.receivedAt ? formatIsoDate(t, q.receivedAt) : '—'}</TableCell>
                  <TableCell className="whitespace-pre-wrap text-sm text-muted-foreground">{q.note}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" className="h-8" onClick={() => onFiles(q)}>
                      <Paperclip className="h-4 w-4" />
                      {t('CostControl_rfq_quote_attachments', { n: q.attachments.length })}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {canAwardQuote(af, q) && (
                        <Button variant="outline" size="sm" className="h-8" onClick={() => onAward(q)}>
                          <Trophy className="h-4 w-4" />
                          {t('CostControl_rfq_award')}
                        </Button>
                      )}
                      {af.editQuotes && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t('CostControl_rfq_more')}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => onEdit(q)}>
                              <Pencil className="h-4 w-4" />
                              {t('Common_edit')}
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => onDelete(q)}>
                              <Trash2 className="h-4 w-4" />
                              {t('Common_delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}
export default RfqQuotesCard
